import type { DeletePageModalProps } from "@v2/types/modalTypes";

import React from "react";

import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { funnelUtils } from "@v2/trpc/routes/funnel";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { useNavigate } from "react-router-dom";

export const DeletePageModal: React.FC<
  DeletePageModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ page, open, onOpenChange }) => {
  const isMobile = useIsMobile();
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();

  const { mutate: deletePage, isPending } =
    nextJSProjectRouter.deletePage.useMutation({
      onSuccess: () => {
        void nextJSProjectUtils.listPages.invalidate({ projectId });
        void funnelUtils.list.invalidate({ projectId });
        void funnelUtils.find.invalidate({ projectId });

        toast.success({
          title: "Page Deleted",
          description: `${page.name} has been deleted successfully! 🎉`,
        });

        onOpenChange(false);

        if (projectId) {
          navigate(`/shops/${projectId}/home`);
        }
      },
    });

  const handleDelete = () => {
    if (!projectId) {
      return;
    }

    logEvent("page.delete");

    // Check if the page is currently published
    if (page.lastPublishedFileVersionId) {
      toast.error({
        title: "Cannot Delete Published Page",
        description:
          "You must unpublish this page before you can delete it. Published pages cannot be deleted.",
      });
      return;
    }

    deletePage({
      projectId,
      id: page.id,
    });
  };

  const title = `Delete ${page.name}`;
  const description =
    "This page will be permanently removed from your store. You won't be able to recover it once deleted.";

  const footer = (
    <div className="flex flex-row gap-2 justify-end w-full">
      <Button
        variant="secondary"
        size="base"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        variant="destructive"
        size="base"
        onClick={handleDelete}
        loading={isPending}
        data-testid="delete-page-button"
      >
        Delete Page
      </Button>
    </div>
  );

  const content = (
    <Alert
      variant="error"
      description="This action is permanent and cannot be undone."
    />
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        footer={footer}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      title={title}
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      showCloseButton={false}
      description={description}
      footer={footer}
    >
      {content}
    </Modal>
  );
};
